import type { DayPlan, Itinerary, Mode, Stop, TravelMode } from "./types";

/**
 * Lenker som tar ruta ut av appen og inn i bilen.
 *
 * Appen kan si hvor man skal og hvorfor, men den skal ikke prøve å være en
 * navigasjonsapp: den har ingen veidata, ingen trafikkflyt og ingen stemme.
 * Det Google Maps og Apple Kart gjør bedre, skal de få gjøre — og den korteste
 * veien dit er en lenke med stoppene i rekkefølge.
 *
 * Grensen på antall veipunkter er ikke vår, men deres: Google Maps tar 10
 * (start, mål og åtte underveis), Apple tar bare start og mål. Er dagen lengre
 * enn det, kuttes den jevnt framfor å kappes bakfra — en dagsetappe uten
 * målet er ikke en rute.
 */

/** Google Maps tar start, mål og åtte veipunkter mellom dem. */
const GOOGLE_MAX = 10;

/**
 * Plukker `count` stopp jevnt fordelt, med start og mål alltid med.
 *
 * Samme problem som `sampleEvenly` i ruteplanleggeren løser, og samme svar:
 * de første ni stoppene av tolv er ikke dagens rute, det er begynnelsen av den.
 */
function trim(stops: Stop[], count: number): Stop[] {
  if (stops.length <= count) return stops;
  if (count <= 2) return [stops[0], stops[stops.length - 1]];

  const middle = stops.slice(1, -1);
  const wanted = count - 2;
  const step = (middle.length - 1) / (wanted - 1 || 1);
  const picked = Array.from(
    { length: wanted },
    (_, index) => middle[Math.round(index * step)],
  );

  return [stops[0], ...picked, stops[stops.length - 1]];
}

/** `lat,lon` med nok desimaler til å treffe, og ikke flere. */
function point(stop: { lat: number; lon: number }): string {
  return `${stop.lat.toFixed(5)},${stop.lon.toFixed(5)}`;
}

/**
 * Reisemåten oversatt til Google Maps' eget vokabular.
 *
 * Tog og buss er samme oppslag der: `transit` gir alt som går rutegående på
 * strekningen, og lar man bruker velge. Å be om bare tog ville skjult
 * kystbussen på strekninger der det ikke går skinner i det hele tatt.
 */
/**
 * Apple Maps sin egen kode for reisemåte.
 *
 * «d» er kjøring, «r» kollektivtransport, «w» gange. Egen tabell framfor
 * gjenbruk av Googles verdier: de to tjenestene bruker ulike navn, og en
 * oversettelse mellom dem ville vært et sted å ta feil.
 */
const APPLE_DIRFLG: Record<Mode | "kollektiv", string> = {
  kollektiv: "r",
  bil: "d",
  tog: "r",
  buss: "r",
  fottur: "w",
};

const TRAVEL_MODE: Record<Mode | "kollektiv", string> = {
  kollektiv: "transit",
  bil: "driving",
  tog: "transit",
  buss: "transit",
  fottur: "walking",
};

/**
 * Veibeskrivelse for én etappe mellom to nabostopp.
 *
 * Skiller seg fra `googleMapsUrl` ved å svare på et annet spørsmål. Den er
 * dagens kjøring, med veipunkter, ment for å starte navigasjonen. Denne er
 * «hvordan kommer jeg meg herfra og dit» — ett hopp, i den reisemåten
 * etappen faktisk er, og uten `dir_action=navigate`: den som lurer på om det
 * går et tog vil se alternativene, ikke få stemmen i gang.
 */
export function directionsUrl(
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
  mode: Mode,
): string {
  const params = new URLSearchParams({
    api: "1",
    origin: point(from),
    destination: point(to),
    travelmode: TRAVEL_MODE[mode] ?? "driving",
  });

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/**
 * Google Maps-rute for en dagsetappe.
 *
 * `dir_action=navigate` starter navigasjonen med en gang i appen, og gjør
 * ingenting i nettleseren — nettopp riktig, siden det er på telefonen man
 * trykker dette.
 */
export function googleMapsUrl(stops: Stop[]): string | null {
  const route = trim(stops, GOOGLE_MAX);
  if (route.length < 2) return null;

  const params = new URLSearchParams({
    api: "1",
    origin: point(route[0]),
    destination: point(route[route.length - 1]),
    travelmode: "driving",
    dir_action: "navigate",
  });

  const waypoints = route.slice(1, -1);
  if (waypoints.length > 0) params.set("waypoints", waypoints.map(point).join("|"));

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/**
 * Apple Kart-rute for en dagsetappe.
 *
 * Apple støtter ikke veipunkter i URL-skjemaet sitt, så lenken går fra dagens
 * første til dagens siste stopp. Det er en ærligere forenkling enn å slippe
 * veipunktene stille: navnet på start og mål står i lenken, så brukeren ser
 * hva de får.
 *
 * `maps.apple.com` fungerer på iOS og macOS, og faller tilbake til et kart i
 * nettleseren andre steder.
 */
export function appleMapsUrl(stops: Stop[], mode: TravelMode): string | null {
  if (stops.length < 2) return null;
  const from = stops[0];
  const to = stops[stops.length - 1];

  const params = new URLSearchParams({
    saddr: point(from),
    daddr: point(to),
    /*
     * `dirflg` sto hardkodet som «d» — kjøring — for hver reisemåte, så en
     * togtur åpnet Apple Maps med kjørebeskrivelse. Feilen fantes før
     * kollektivvalget og ble funnet under kartleggingen av det: en lenke som
     * ser ut som den virker og svarer på et annet spørsmål.
     *
     * Apple bruker «r» for kollektivtransport og «w» for gange.
     */
    dirflg: APPLE_DIRFLG[mode] ?? "d",
  });

  return `https://maps.apple.com/?${params.toString()}`;
}

/** Stoppene i en dag, i rekkefølge, med gårsdagens siste som startpunkt. */
export function dayRoute(itinerary: Itinerary, dayIndex: number): Stop[] {
  const day: DayPlan | undefined = itinerary.days[dayIndex];
  if (!day) return [];

  /*
   * Dagen starter der forrige dag sluttet. Uten det blir dag 2 en rute mellom
   * to stopp man alt står ved, og selve kjøringen — den fra i går kveld til i
   * dag morgen — mangler.
   */
  const previous = itinerary.days[dayIndex - 1]?.stops.at(-1);
  const stops = previous ? [previous, ...day.stops] : [...day.stops];

  // Samme sted to ganger gir et veipunkt på null meter.
  return stops.filter((stop, index) => index === 0 || stop.id !== stops[index - 1].id);
}

/**
 * Hele reisen som GPX 1.1.
 *
 * Både som rute (`rte`) og som veipunkter (`wpt`): en GPS-klokke og en
 * håndholdt Garmin leser rute, mens de fleste kartapper viser veipunkter med
 * navn. Skriver man bare det ene, mangler halvparten av utstyret det de bruker.
 *
 * Høyden er med fordi den finnes i dataene, og fordi en profil er hele poenget
 * med å ta en fjellrute inn på klokka.
 */
export function itineraryToGpx(itinerary: Itinerary): string {
  const stops = itinerary.days.flatMap((day) => day.stops);
  const name = `${itinerary.corridor.startName}–${itinerary.corridor.endName}`;

  const waypoints = stops
    .map((stop) =>
      [
        `  <wpt lat="${stop.lat.toFixed(6)}" lon="${stop.lon.toFixed(6)}">`,
        `    <ele>${stop.elevationM}</ele>`,
        `    <name>${escapeXml(stop.name)}</name>`,
        `    <desc>${escapeXml(stop.blurb)}</desc>`,
        `  </wpt>`,
      ].join("\n"),
    )
    .join("\n");

  const points = stops
    .map(
      (stop) =>
        `    <rtept lat="${stop.lat.toFixed(6)}" lon="${stop.lon.toFixed(6)}">` +
        `<ele>${stop.elevationM}</ele><name>${escapeXml(stop.name)}</name></rtept>`,
    )
    .join("\n");

  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<gpx version="1.1" creator="reise-planer" xmlns="http://www.topografiske.no/gpx"`,
    `     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">`,
    `  <metadata>`,
    `    <name>${escapeXml(name)}</name>`,
    `    <desc>${escapeXml(itinerary.corridor.tagline)}</desc>`,
    `  </metadata>`,
    waypoints,
    `  <rte>`,
    `    <name>${escapeXml(name)}</name>`,
    points,
    `  </rte>`,
    `</gpx>`,
    ``,
  ].join("\n");
}

/** Én dag som GPX, for den som bare skal ha morgendagens etappe på klokka. */
export function dayToGpx(itinerary: Itinerary, dayIndex: number): string {
  const stops = dayRoute(itinerary, dayIndex);
  const day = itinerary.days[dayIndex];
  const name = `${itinerary.corridor.name} — ${day?.date ?? `dag ${dayIndex + 1}`}`;

  return itineraryToGpx({
    ...itinerary,
    corridor: { ...itinerary.corridor, startName: stops[0]?.name ?? "", endName: stops.at(-1)?.name ?? "", tagline: name },
    days: [{ ...day, stops }],
  } as Itinerary);
}

/**
 * XML tåler ikke `&` og `<` i tekst, og stedsbeskrivelsene inneholder begge.
 * Uten dette blir filen ugyldig, og en GPS avviser den uten å si hvorfor.
 */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Laster ned en tekstfil. Blob framfor data-URL: filnavnet overlever. */
export function downloadFile(name: string, contents: string, type: string): void {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Uten dette holder nettleseren blobben i minnet resten av økta.
  URL.revokeObjectURL(url);
}
